import { useCallback, useEffect, useState } from 'react'

import {
  getStoreManagementSettings,
  updateStoreManagementSettings,
} from '@/lib/api/client/settings'
import { SwitchTrack } from '@/components/ui/Switch'
import type { StoreManagementSettings as StoreManagementSettingsValue } from '@/lib/api/schemas/store-management.schema'

const SWITCHES: ReadonlyArray<{
  key: keyof StoreManagementSettingsValue
  label: string
}> = [
  { key: 'ymarketPricesEnabled', label: 'Цены на Яндекс Маркете' },
  { key: 'ymarketStockEnabled', label: 'Остатки на Яндекс Маркете' },
  { key: 'wildberriesPricesEnabled', label: 'Цены на Wildberries' },
  { key: 'wildberriesStockEnabled', label: 'Остатки на Wildberries' },
]

function message(reason: unknown): string {
  return reason instanceof Error && reason.message.length > 0
    ? reason.message
    : 'Не удалось изменить настройку управления витриной.'
}

export function StoreManagementSettings() {
  const [settings, setSettings] = useState<StoreManagementSettingsValue | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<keyof StoreManagementSettingsValue | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setSettings(await getStoreManagementSettings())
    } catch (reason: unknown) {
      setError(message(reason))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const toggle = async (key: keyof StoreManagementSettingsValue) => {
    if (settings === null || busy !== null) return
    setBusy(key)
    setError(null)
    try {
      setSettings(await updateStoreManagementSettings({ ...settings, [key]: !settings[key] }))
    } catch (reason: unknown) {
      setError(message(reason))
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="space-y-4" aria-labelledby="store-management-settings-title">
      <div>
        <h2 id="store-management-settings-title" className="text-[18px] font-semibold tracking-tight text-ink">
          Управление ценами и остатками через API
        </h2>
        <p className="mt-0.5 text-[13px] text-ink-muted">
          Каналы выключены по умолчанию. Включайте запись только после проверки предпросмотра.
        </p>
      </div>
      {error !== null && <p role="alert" className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">{error}</p>}
      {loading && <p role="status" className="rounded-lg border border-rule bg-paper p-5 text-sm text-ink-muted">Загружаем настройки управления витриной…</p>}
      {!loading && settings !== null && (
        <div className="surface-card grid gap-1 rounded-md p-2">
          {SWITCHES.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              role="switch"
              aria-checked={settings[key]}
              className="flex min-h-12 items-center justify-between gap-4 rounded-sm px-3 py-2 text-left text-[13px] text-ink-2 hover:bg-bone/60 disabled:opacity-60"
              disabled={busy !== null}
              onClick={() => { void toggle(key) }}
            >
              <span>{label}</span>
              <SwitchTrack checked={settings[key]} />
            </button>
          ))}
        </div>
      )}
    </section>
  )
}
